import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import {
  Zap, ArrowRight, Loader2, AlertTriangle, CheckCircle,
  TrendingUp, TrendingDown, ShieldAlert, Shield, ChevronDown,
  ArrowUpRight, Menu, X,
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

/** Fire-and-forget PostHog capture — never blocks UI, never throws */
const ph = (event, props) => { try { window.posthog?.capture(event, props); } catch (_) { /* silent */ } };

const TRADES = [
  { value: '', label: 'Any / General' },
  { value: 'electrical', label: 'Electrical' },
  { value: 'hvac', label: 'HVAC' },
  { value: 'plumbing', label: 'Plumbing' },
  { value: 'mechanical', label: 'Mechanical' },
  { value: 'civils', label: 'Civils' },
  { value: 'telecom', label: 'Telecom' },
  { value: 'fire_protection', label: 'Fire Protection' },
];

const RISK_STYLES = {
  critical: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', icon: AlertTriangle },
  high: { bg: 'bg-red-500/10', border: 'border-red-500/30', text: 'text-red-400', icon: AlertTriangle },
  elevated: { bg: 'bg-amber-500/10', border: 'border-amber-500/30', text: 'text-amber-400', icon: ShieldAlert },
  low: { bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', text: 'text-emerald-400', icon: Shield },
};

function fmt(n) {
  if (n === undefined || n === null) return '—';
  return n.toLocaleString('en-GB', { maximumFractionDigits: 0 });
}

export default function QuickCheckPage() {
  const [form, setForm] = useState({ contract_value: '', estimated_cost: '', competition_level: 'medium', trade_type: '' });
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [mobileMenu, setMobileMenu] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [extractedFields, setExtractedFields] = useState(null);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [riskFields, setRiskFields] = useState({
    payment_terms_days: null,
    has_ld_clause: null,
    ld_rate_per_day: null,
    retention_pct: null,
  });

  const handleCheckout = async (plan) => {
    setCheckoutLoading(true);
    ph('payment_started', { plan, source: 'quick_check_cta', quick_check_id: result?.quick_check_id });
    try {
      const originUrl = window.location.origin;
      const res = await axios.post(`${API}/api/billing/public-checkout?plan=${plan}&origin_url=${encodeURIComponent(originUrl)}`);
      if (res.data.checkout_url) {
        window.top.location.href = res.data.checkout_url;
      }
    } catch (err) {
      toast.error('Failed to start checkout — please try again');
    } finally {
      setCheckoutLoading(false);
    }
  };

  const handleFileUpload = async (file) => {
    if (!file) return;
    setUploadedFile(file);
    setExtracting(true);
    setExtractedFields(null);

    const formData = new FormData();
    formData.append('file', file);

    try {
      const res = await axios.post(`${API}/api/quick-check/extract`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      const d = res.data;

      if (d.contract_value) setForm(f => ({ ...f, contract_value: String(d.contract_value) }));
      if (d.trade_type) setForm(f => ({ ...f, trade_type: d.trade_type }));
      if (d.competition_level) setForm(f => ({ ...f, competition_level: d.competition_level }));

      setRiskFields({
        payment_terms_days: d.payment_terms_days,
        has_ld_clause: d.has_ld_clause,
        ld_rate_per_day: d.ld_rate_per_day,
        retention_pct: d.retention_pct,
      });

      setExtractedFields(d);
      ph('doc_extracted', { confidence: d.confidence, has_contract_value: !!d.contract_value });
    } catch (err) {
      console.error('Extraction failed:', err);
      toast.error(err.response?.data?.detail || 'Could not extract from document — fill in manually');
    } finally {
      setExtracting(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    const cv = parseFloat(form.contract_value);
    const ec = parseFloat(form.estimated_cost);
    if (!cv || cv <= 0) { toast.error('Enter a valid contract value'); return; }
    if (!ec || ec <= 0) { toast.error('Enter a valid estimated cost'); return; }

    ph('check_started', {
      competition_level: form.competition_level,
      trade_type: form.trade_type || null,
      has_contract_value: cv > 0,
      has_estimated_cost: ec > 0,
    });

    setLoading(true);
    setResult(null);
    try {
      const res = await axios.post(`${API}/api/quick-check/run`, {
        contract_value: cv,
        estimated_cost: ec,
        competition_level: form.competition_level,
        trade_type: form.trade_type || null,
        ...riskFields,
      });
      setResult(res.data);

      ph('check_completed', {
        quick_check_id: res.data.quick_check_id,
        verdict: res.data.decision?.headline,
        margin_pct: res.data.impact?.current_margin_pct,
        competition_level: form.competition_level,
      });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Check failed — please try again');
    } finally {
      setLoading(false);
    }
  };

  const reset = () => {
    setResult(null);
    setForm({ contract_value: '', estimated_cost: '', competition_level: 'medium', trade_type: '' });
    setUploadedFile(null);
    setExtractedFields(null);
    setRiskFields({ payment_terms_days: null, has_ld_clause: null, ld_rate_per_day: null, retention_pct: null });
  };

  const risk = result?.decision?.risk_level;
  const rs = risk ? RISK_STYLES[risk] || RISK_STYLES.elevated : null;
  const RiskIcon = rs?.icon || ShieldAlert;

  return (
    <div className="min-h-screen bg-slate-950 text-white" data-testid="quick-check-page">
      {/* Minimal Nav */}
      <header className="fixed top-0 left-0 right-0 h-14 bg-slate-950/90 backdrop-blur-md border-b border-slate-800/50 z-50">
        <div className="max-w-5xl mx-auto h-full px-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <Zap className="w-5 h-5 text-orange-500" />
            <span className="font-black text-base tracking-tight" style={{ fontFamily: 'Barlow Condensed' }}>BIDSENTRY</span>
          </Link>
          <nav className="hidden md:flex items-center gap-6">
            <Link to="/pricing" className="text-xs text-slate-500 hover:text-slate-300 transition-colors">Pricing</Link>
            <Link to="/auth" className="text-xs text-slate-500 hover:text-slate-300 transition-colors">Sign In</Link>
            <Link to="/auth?mode=register">
              <Button size="sm" className="bg-orange-500 hover:bg-orange-600 text-white text-xs h-8 px-4" data-testid="nav-register-btn">
                Create Account
              </Button>
            </Link>
          </nav>
          <Button variant="ghost" size="sm" className="md:hidden text-slate-400" onClick={() => setMobileMenu(!mobileMenu)}>
            {mobileMenu ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </Button>
        </div>
        {mobileMenu && (
          <div className="md:hidden bg-slate-900 border-b border-slate-800 p-4 space-y-3">
            <Link to="/pricing" className="block text-sm text-slate-400" onClick={() => setMobileMenu(false)}>Pricing</Link>
            <Link to="/auth" className="block text-sm text-slate-400" onClick={() => setMobileMenu(false)}>Sign In</Link>
            <Link to="/auth?mode=register" onClick={() => setMobileMenu(false)}>
              <Button size="sm" className="w-full bg-orange-500 hover:bg-orange-600 text-white text-xs">Create Account</Button>
            </Link>
          </div>
        )}
      </header>

      <main className="pt-14">
        {/* ---------- INPUT PHASE ---------- */}
        {!result && (
          <section className="min-h-[calc(100vh-3.5rem)] flex items-center justify-center px-4 py-12">
            <div className="w-full max-w-md">
              <div className="text-center mb-8">
                <h1
                  className="text-3xl sm:text-4xl font-black tracking-tight mb-3"
                  style={{ fontFamily: 'Barlow Condensed' }}
                  data-testid="quick-check-headline"
                >
                  ANALYSE YOUR CONTRACT
                </h1>
                <p className="text-sm text-slate-400 max-w-sm mx-auto leading-relaxed">
                  Upload your ITT or tender document and we'll read it for you — or enter details manually.
                </p>
              </div>

              <form onSubmit={submit} className="space-y-4" data-testid="quick-check-form">
                {/* Document Upload Zone */}
                <div
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={e => { e.preventDefault(); setDragOver(false); handleFileUpload(e.dataTransfer.files[0]); }}
                  onClick={() => document.getElementById('doc-upload').click()}
                  className={`border-2 border-dashed rounded-lg p-5 text-center cursor-pointer transition-all ${
                    dragOver
                      ? 'border-blue-500/60 bg-blue-500/5'
                      : 'border-slate-700/50 bg-slate-900/30 hover:border-slate-600'
                  }`}
                  data-testid="doc-upload-zone"
                >
                  <input
                    id="doc-upload"
                    type="file"
                    accept=".pdf,.doc,.docx"
                    style={{ display: 'none' }}
                    onChange={e => handleFileUpload(e.target.files[0])}
                  />
                  {extracting ? (
                    <div className="flex items-center justify-center gap-2">
                      <Loader2 className="w-4 h-4 animate-spin text-blue-400" />
                      <p className="text-sm text-blue-400 m-0">Reading document...</p>
                    </div>
                  ) : uploadedFile ? (
                    <p className="text-sm text-emerald-400 m-0">
                      <CheckCircle className="w-4 h-4 inline mr-1.5 -mt-0.5" />
                      {uploadedFile.name}
                      {extractedFields?.confidence != null && ` — ${Math.round(extractedFields.confidence * 100)}% confidence`}
                    </p>
                  ) : (
                    <>
                      <p className="text-sm text-slate-500 m-0 mb-1">
                        Drop your ITT or tender document here
                      </p>
                      <p className="text-[11px] text-slate-600 m-0">
                        PDF or Word · Fields pre-filled automatically
                      </p>
                    </>
                  )}
                </div>

                {extractedFields?.notes && (
                  <p className="text-[11px] text-slate-500 -mt-2" data-testid="extraction-notes">
                    {extractedFields.notes}
                  </p>
                )}
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-medium mb-1.5 block">
                    Contract Value (Your Bid Price)
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-mono">£</span>
                    <Input
                      type="number"
                      value={form.contract_value}
                      onChange={(e) => setForm(f => ({ ...f, contract_value: e.target.value }))}
                      placeholder="e.g. 250,000"
                      className="pl-7 bg-slate-900 border-slate-700 text-white text-lg font-mono h-12 placeholder:text-slate-600"
                      data-testid="input-contract-value"
                    />
                  </div>
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-medium mb-1.5 block">
                    Your Estimated Cost
                  </label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 text-sm font-mono">£</span>
                    <Input
                      type="number"
                      value={form.estimated_cost}
                      onChange={(e) => setForm(f => ({ ...f, estimated_cost: e.target.value }))}
                      placeholder="e.g. 215,000"
                      className="pl-7 bg-slate-900 border-slate-700 text-white text-lg font-mono h-12 placeholder:text-slate-600"
                      data-testid="input-estimated-cost"
                    />
                  </div>
                  <p className="text-[11px] text-slate-500 mt-1">
                    {uploadedFile
                      ? "The one number only you know — everything else we read from your document."
                      : "What do you expect this contract to cost you to deliver?"}
                  </p>
                </div>

                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-medium mb-1.5 block">
                    Competition Level
                  </label>
                  <div className="grid grid-cols-3 gap-2" data-testid="input-competition-level">
                    {['low', 'medium', 'high'].map((level) => (
                      <button
                        key={level}
                        type="button"
                        onClick={() => setForm(f => ({ ...f, competition_level: level }))}
                        className={`py-2.5 rounded-sm text-xs font-bold uppercase tracking-wider border transition-all ${
                          form.competition_level === level
                            ? 'bg-orange-500/15 border-orange-500/40 text-orange-400'
                            : 'bg-slate-900 border-slate-700 text-slate-500 hover:border-slate-600'
                        }`}
                        data-testid={`competition-${level}`}
                      >
                        {level}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Optional trade type */}
                <div>
                  <button
                    type="button"
                    onClick={() => setShowAdvanced(!showAdvanced)}
                    className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-600 hover:text-slate-400 transition-colors"
                  >
                    <ChevronDown className={`w-3 h-3 transition-transform ${showAdvanced ? 'rotate-180' : ''}`} />
                    Trade type (optional)
                  </button>
                  {showAdvanced && (
                    <select
                      value={form.trade_type}
                      onChange={(e) => setForm(f => ({ ...f, trade_type: e.target.value }))}
                      className="mt-2 w-full bg-slate-900 border border-slate-700 text-slate-300 text-sm px-3 py-2.5 rounded-sm focus:outline-none focus:ring-1 focus:ring-orange-500"
                      data-testid="input-trade-type"
                    >
                      {TRADES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                    </select>
                  )}
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold text-base h-12 mt-2"
                  data-testid="run-check-btn"
                >
                  {loading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>
                      Run Bid Check
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>

                <p className="text-[10px] text-slate-600 text-center">
                  No sign-up required. Your data is not stored unless you create an account.
                </p>
              </form>
            </div>
          </section>
        )}

        {/* ---------- RESULT PHASE ---------- */}
        {result && (
          <section className="max-w-2xl mx-auto px-4 py-12 space-y-6" data-testid="quick-check-results">
            {/* Decision Banner */}
            <div
              className={`rounded-sm border p-6 ${rs?.bg} ${rs?.border}`}
              data-testid="decision-banner"
            >
              <div className="flex items-start gap-4">
                <RiskIcon className={`w-7 h-7 ${rs?.text} flex-shrink-0 mt-0.5`} />
                <div className="flex-1">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-1">Decision</p>
                  <h2
                    className="text-xl sm:text-2xl font-black tracking-tight mb-1"
                    style={{ fontFamily: 'Barlow Condensed' }}
                    data-testid="decision-headline"
                  >
                    {result.decision.headline}
                  </h2>
                  <p className={`text-xs font-bold uppercase tracking-wider ${rs?.text}`} data-testid="decision-risk-label">
                    {result.decision.risk_label}
                  </p>
                </div>
              </div>
            </div>

            {/* Impact Metrics */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3" data-testid="impact-metrics">
              <MetricCard label="Current Margin" value={`${result.impact.current_margin_pct}%`} sub={`Target: ${result.impact.target_band}`}
                color={result.impact.current_margin_pct < 10 ? 'text-red-400' : result.impact.current_margin_pct < 15 ? 'text-amber-400' : 'text-emerald-400'} />
              <MetricCard label="Profit" value={`£${fmt(result.impact.current_profit)}`} sub="At current pricing"
                color={result.impact.current_profit < 0 ? 'text-red-400' : 'text-slate-200'} />
              <MetricCard label="Downside Exposure" value={`£${fmt(result.impact.estimated_downside)}`} sub="If costs overrun 10%"
                color="text-amber-400" />
              <MetricCard label="Overrun Margin" value={`${result.impact.overrun_margin_pct}%`} sub="After 10% cost rise"
                color={result.impact.overrun_margin_pct < 0 ? 'text-red-400' : 'text-amber-400'} />
            </div>

            {/* Contract Risk Flags */}
            {result.risk_factors && result.risk_factors.length > 0 && (
              <div className="bg-white/[0.02] border border-white/[0.08] rounded-lg px-5 py-4" data-testid="risk-factors">
                <p className="text-[11px] font-bold tracking-[0.08em] text-white/40 uppercase mb-3">
                  Contract Risk Flags
                </p>
                {result.risk_factors.map((rf, i) => (
                  <div key={i} className={`flex items-start gap-2.5 ${i < result.risk_factors.length - 1 ? 'mb-2.5' : ''}`}>
                    <span className="text-sm leading-relaxed flex-shrink-0" style={{
                      color: rf.severity === 'high' ? '#f87171' : rf.severity === 'elevated' ? '#fbbf24' : '#3dd68c'
                    }}>&#9888;</span>
                    <div>
                      <span className="text-[13px] font-semibold text-white/75">{rf.factor}</span>
                      <span className="text-[13px] text-white/40"> — {rf.detail}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Action Card */}
            {result.action.has_adjustment && (
              <Card className="bg-slate-900 border-orange-500/30" data-testid="action-card">
                <CardContent className="py-5 px-6">
                  <p className="text-[10px] uppercase tracking-[0.18em] text-slate-500 mb-2">Recommended Action</p>
                  <div className="flex items-baseline gap-3 mb-2">
                    <span className="text-2xl font-black text-orange-400 font-mono" style={{ fontFamily: 'Barlow Condensed' }}>
                      +£{fmt(result.action.suggested_adjustment_amount)}
                    </span>
                    <span className="text-sm text-slate-400">
                      (+{result.action.suggested_adjustment_pct}%)
                    </span>
                  </div>
                  <p className="text-sm text-slate-400">
                    Increase your bid to <span className="text-white font-mono font-bold">£{fmt(result.action.adjusted_price)}</span> to
                    reach a <span className="text-white font-bold">{result.action.adjusted_margin_pct}%</span> margin.
                  </p>
                </CardContent>
              </Card>
            )}

            {/* Before / After */}
            <div className="grid sm:grid-cols-2 gap-3" data-testid="before-after">
              <ScenarioCard
                label="If Unchanged"
                margin={result.before_after.if_unchanged.margin_pct}
                risk={result.before_after.if_unchanged.risk}
                outcome={result.before_after.if_unchanged.outcome}
                variant="before"
              />
              <ScenarioCard
                label="If Adjusted"
                margin={result.before_after.if_adjusted.margin_pct}
                risk={result.before_after.if_adjusted.risk}
                outcome={result.before_after.if_adjusted.outcome}
                variant="after"
              />
            </div>

            {/* AI Explanation */}
            {result.ai_explanation && (
              <div className="bg-slate-900/60 border border-slate-800 rounded-sm p-5" data-testid="ai-explanation">
                <p className="text-[10px] uppercase tracking-[0.18em] text-slate-600 mb-2">Commercial Context</p>
                <p className="text-sm text-slate-300 leading-relaxed">{result.ai_explanation}</p>
              </div>
            )}

            {/* Confidence */}
            <p className="text-[10px] text-slate-600 text-center" data-testid="confidence-note">
              {result.confidence.note} · Model: {result.meta.model_version}
            </p>

            {/* Decision Feedback */}
            <DecisionFeedback quickCheckId={result.quick_check_id} />

            {/* --- OVO: Risk Nudge Panel (highest-risk signal + £9 CTA) --- */}
            <RiskNudge result={result} onCheckout={() => handleCheckout('single')} checkoutLoading={checkoutLoading} />

            {/* Email Capture + CTA */}
            <EmailCapture quickCheckId={result.quick_check_id} />

            {/* CTA: Continue with paid check */}
            <div className="bg-slate-900 border border-orange-500/20 rounded-sm p-6 text-center space-y-4" data-testid="post-result-cta">
              <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-1">Continue with this bid</p>
              <p className="text-sm text-slate-300">
                Get a full cost breakdown, detailed risk analysis, and saved history for this bid.
              </p>
              <Button
                onClick={() => handleCheckout('single')}
                disabled={checkoutLoading}
                className="w-full bg-orange-500 hover:bg-orange-600 text-white font-bold h-12 text-base"
                data-testid="cta-buy-bid-check"
              >
                {checkoutLoading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Unlock Full Bid Check — £9
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </>
                )}
              </Button>
              <div className="flex flex-col sm:flex-row gap-3 justify-center pt-1">
                <Link to="/pricing">
                  <Button variant="outline" className="border-slate-700 text-slate-400 hover:text-white text-xs" data-testid="cta-view-pricing">
                    Or go unlimited from £149/mo
                    <ArrowUpRight className="w-4 h-4 ml-1.5" />
                  </Button>
                </Link>
                <Button variant="ghost" onClick={reset} className="text-slate-500 hover:text-white text-xs" data-testid="cta-run-another">
                  Run Another Check
                </Button>
              </div>
              <p className="text-[10px] text-slate-600">
                Secure payment via Stripe. No account required.
              </p>
            </div>

            {/* Footer trust */}
            <p className="text-[10px] text-slate-700 text-center pb-8">
              Built on a deterministic financial model. Not AI guesswork.<br />
              Bidsentry Ltd &bull; Company No: 17096278 &bull; 124 City Road, London, EC1V 2NX, United Kingdom
            </p>
          </section>
        )}
      </main>
    </div>
  );
}


/* ------------------------------------------------------------------ */
/* Sub-components                                                       */
/* ------------------------------------------------------------------ */

function MetricCard({ label, value, sub, color = 'text-slate-200' }) {
  return (
    <div className="bg-slate-900 border border-slate-800 rounded-sm p-4">
      <p className="text-[9px] uppercase tracking-[0.15em] text-slate-500 mb-1">{label}</p>
      <p className={`text-lg font-black font-mono ${color}`} style={{ fontFamily: 'Barlow Condensed' }}>{value}</p>
      {sub && <p className="text-[10px] text-slate-600 mt-0.5">{sub}</p>}
    </div>
  );
}

function ScenarioCard({ label, margin, risk, outcome, variant }) {
  const isBefore = variant === 'before';
  const Icon = isBefore ? TrendingDown : TrendingUp;
  const borderColor = isBefore ? 'border-slate-800' : 'border-emerald-500/20';
  const iconColor = isBefore ? 'text-slate-500' : 'text-emerald-500';

  return (
    <div className={`bg-slate-900 border ${borderColor} rounded-sm p-4`}>
      <div className="flex items-center gap-2 mb-2">
        <Icon className={`w-4 h-4 ${iconColor}`} />
        <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500 font-medium">{label}</p>
      </div>
      <p className="text-lg font-black font-mono text-slate-200 mb-1" style={{ fontFamily: 'Barlow Condensed' }}>
        {margin}% margin
      </p>
      <p className="text-xs text-slate-400 leading-relaxed">{outcome}</p>
    </div>
  );
}


function DecisionFeedback({ quickCheckId }) {
  const [answered, setAnswered] = useState(false);

  const respond = (response) => {
    ph('decision_changed', { quick_check_id: quickCheckId, response });
    setAnswered(true);
  };

  if (answered) {
    return (
      <p className="text-[10px] text-slate-600 text-center" data-testid="decision-feedback-thanks">
        Thanks — noted.
      </p>
    );
  }

  return (
    <div className="bg-slate-900/40 border border-slate-800/60 rounded-sm p-4 text-center" data-testid="decision-feedback">
      <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-3">Will this change how you price this bid?</p>
      <div className="flex justify-center gap-2">
        {[
          { label: 'Yes', value: 'yes' },
          { label: 'No', value: 'no' },
          { label: 'Not sure', value: 'not_sure' },
        ].map((opt) => (
          <button
            key={opt.value}
            onClick={() => respond(opt.value)}
            className="px-4 py-1.5 rounded-sm text-xs font-medium border border-slate-700 text-slate-400 hover:border-orange-500/40 hover:text-orange-400 transition-colors"
            data-testid={`decision-feedback-${opt.value}`}
          >
            {opt.label}
          </button>
        ))}
      </div>
    </div>
  );
}


function RiskNudge({ result, onCheckout, checkoutLoading }) {
  const risk = result?.decision?.risk_level;
  const margin = result?.impact?.current_margin_pct;
  const targetBand = result?.impact?.target_band;
  const overrunMargin = result?.impact?.overrun_margin_pct;
  const adjustment = result?.action?.suggested_adjustment_amount;
  const isRisky = risk && risk !== 'low';

  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useEffect(() => {
    if (isRisky) {
      ph('nudge_shown', { risk_level: risk, margin_pct: margin, quick_check_id: result?.quick_check_id });
    }
  }, [isRisky, risk, margin, result?.quick_check_id]);

  if (!isRisky) return null;

  // Build the highest-impact risk signal
  let signal = '';
  if (risk === 'critical') {
    signal = 'This bid will produce a net loss at current pricing.';
  } else if (risk === 'high') {
    signal = `Your margin of ${margin}% is significantly below the ${targetBand} target — a 10% cost overrun drops it to ${overrunMargin}%.`;
  } else if (risk === 'elevated') {
    signal = `Your margin of ${margin}% is below the safe band of ${targetBand}.`;
  }

  return (
    <div
      className="relative overflow-hidden rounded-sm border border-red-500/25 bg-gradient-to-r from-red-950/30 to-slate-900 p-5"
      data-testid="ovo-risk-nudge"
    >
      <div className="absolute top-0 left-0 w-1 h-full bg-red-500" />
      <div className="pl-3">
        <div className="flex items-center gap-2 mb-2">
          <AlertTriangle className="w-4 h-4 text-red-400" />
          <span className="text-[10px] uppercase tracking-[0.15em] font-bold text-red-400">Risk Signal</span>
        </div>
        <p className="text-sm text-slate-200 font-medium leading-relaxed mb-3" data-testid="nudge-signal-text">
          {signal}
        </p>
        {adjustment > 0 && (
          <p className="text-xs text-slate-400 mb-4">
            The full analysis includes a recommended price adjustment of <span className="text-orange-400 font-bold">+£{adjustment.toLocaleString('en-GB', { maximumFractionDigits: 0 })}</span> and detailed risk breakdown.
          </p>
        )}
        <Button
          onClick={() => {
            ph('nudge_clicked', { risk_level: risk, quick_check_id: result?.quick_check_id });
            onCheckout();
          }}
          disabled={checkoutLoading}
          className="bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm h-10 px-6"
          data-testid="nudge-checkout-btn"
        >
          {checkoutLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <>
              Unlock Full Bid Check — £9
              <ArrowRight className="w-4 h-4 ml-2" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}


function EmailCapture({ quickCheckId }) {
  const [email, setEmail] = useState('');
  const [sent, setSent] = useState(false);
  const [sending, setSending] = useState(false);

  const capture = async (e) => {
    e.preventDefault();
    if (!email || !email.includes('@')) { toast.error('Enter a valid email'); return; }
    setSending(true);
    try {
      const res = await axios.post(`${API}/api/quick-check/capture-email`, { quick_check_id: quickCheckId, email });
      if (res.data.email_sent) {
        setSent(true);
        toast.success(`Sent to ${email}`);
        ph('email_captured', { quick_check_id: quickCheckId, source: 'post_result' });
      } else {
        toast.error('Email could not be delivered — please try again');
      }
    } catch {
      toast.error('Failed to send — please try again');
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="bg-emerald-500/10 border border-emerald-500/20 rounded-sm p-4 text-center" data-testid="email-captured-success">
        <p className="text-sm text-emerald-400">Sent to {email}</p>
      </div>
    );
  }

  return (
    <form onSubmit={capture} className="bg-slate-900 border border-slate-800 rounded-sm p-4" data-testid="email-capture-form">
      <p className="text-[10px] uppercase tracking-[0.15em] text-slate-500 mb-2 text-center">Get this result in your inbox</p>
      <div className="flex gap-2">
        <Input
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="your@email.com"
          className="bg-slate-800 border-slate-700 text-white text-sm flex-1"
          data-testid="email-capture-input"
        />
        <Button type="submit" disabled={sending} className="bg-orange-500 hover:bg-orange-600 text-white text-sm px-4" data-testid="email-capture-submit">
          {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send'}
        </Button>
      </div>
      <p className="text-[9px] text-slate-600 text-center mt-1.5">We'll email your results. No spam. Unsubscribe anytime.</p>
    </form>
  );
}
